import { http } from '@/lib/http';
import type { Page } from '@/types/common';
import type {
  AcceptInvitePayload,
  AuthSession,
  ChangePasswordPayload,
  CurrentUser,
  ForgotPasswordPayload,
  InvitePreview,
  LoginPayload,
  LoginResult,
  MfaConfirmPayload,
  MfaConfirmResult,
  MfaEnrollResult,
  MfaVerifyPayload,
  RefreshPayload,
  RefreshResult,
  ResetPasswordPayload,
} from '@/types/auth';

const BASE = '/api/v1/auth';

export const authService = {
  // ── Session lifecycle ────────────────────────────────────────────────
  login: (payload: LoginPayload) => http.post<LoginResult, LoginPayload>(`${BASE}/login`, payload),
  refresh: (payload: RefreshPayload) =>
    http.post<RefreshResult, RefreshPayload>(`${BASE}/refresh`, payload),
  logout: () => http.post<{ ok: true }>(`${BASE}/logout`),
  me: () => http.get<CurrentUser>(`${BASE}/me`),

  // ── Password ─────────────────────────────────────────────────────────
  forgotPassword: (payload: ForgotPasswordPayload) =>
    http.post<{ ok: true }, ForgotPasswordPayload>(`${BASE}/forgot-password`, payload),
  resetPassword: (payload: ResetPasswordPayload) =>
    http.post<{ ok: true }, ResetPasswordPayload>(`${BASE}/reset-password`, payload),
  changePassword: (payload: ChangePasswordPayload) =>
    http.post<{ ok: true }, ChangePasswordPayload>(`${BASE}/password/change`, payload),

  // ── Invite ───────────────────────────────────────────────────────────
  invitePreview: (token: string) => http.get<InvitePreview>(`${BASE}/invite/${token}`),
  acceptInvite: (payload: AcceptInvitePayload) =>
    http.post<LoginResult, AcceptInvitePayload>(`${BASE}/accept-invite`, payload),

  // ── MFA ──────────────────────────────────────────────────────────────
  mfaVerify: (payload: MfaVerifyPayload) =>
    http.post<LoginResult, MfaVerifyPayload>(`${BASE}/mfa/verify`, payload),
  mfaEnroll: () => http.post<MfaEnrollResult>(`${BASE}/mfa/enroll`),
  mfaConfirm: (payload: MfaConfirmPayload) =>
    http.post<MfaConfirmResult, MfaConfirmPayload>(`${BASE}/mfa/confirm`, payload),
  mfaDisable: (currentPassword: string) =>
    http.post<{ ok: true }, { currentPassword: string }>(`${BASE}/mfa/disable`, {
      currentPassword,
    }),

  // ── Sessions ─────────────────────────────────────────────────────────
  listSessions: () => http.get<Page<AuthSession>>(`${BASE}/sessions`),
  revokeSession: (sid: string) => http.delete<{ ok: true }>(`${BASE}/sessions/${sid}`),
  revokeOtherSessions: () =>
    http.post<{ ok: true; revoked: number }>(`${BASE}/sessions/revoke-others`),
};
